/**
 * Full-page "New version" authoring route for an existing workflow key, on
 * the Lara Canvas kit shell (BRIEF Phase 2, D1).
 *
 * Unlike the modal (which prefilled from the already-loaded list rows), a
 * standalone route fetches the key's current active version itself, then
 * lets the author edit + publish a new immutable version. Bump logic
 * mirrors the former dialog's `handleNewVersion` — moved, not rewritten.
 * The Name/Description fields and the version selector now live in the kit
 * shell (D2, D4) instead of a form above the canvas.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { AlertTriangle, ArrowLeft, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { PermissionDenied } from "@/components/ui/permission-denied";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { WorkflowAuthor } from "@/components/portal/workflow-flow-v2/WorkflowAuthor";
import {
  WorkflowEditorHeader,
  type WorkflowStatusLabel,
} from "@/components/portal/workflow-flow-v2/WorkflowEditorHeader";
import { workflowJsonEquivalent } from "@/components/portal/workflow-flow-v2/workflowJsonEquivalent";
import { parseWorkflowDsl } from "@/components/portal/workflow-flow-v2/parseWorkflowDsl";
import { publishBlockingIssues, type ValidationIssue } from "@/lib/workflow-validation";
import {
  workflowsApi,
  type ReconcileResponse,
  type DefinitionResponse,
  type NewVersionRequest,
} from "@/services/api/workflows";
import {
  draftPrefill,
  markDraftPublished,
  useWorkflowDraft,
} from "@/components/portal/workflows/useWorkflowDraft";

export const Route = createFileRoute(
  "/portal/workflows/definitions_/$key/new-version",
)({
  component: NewVersionPage,
  // `?draft=<id>` lays a Claude draft over the active version
  // (mcp-artefact-creation-pilot D15).
  validateSearch: (search: Record<string, unknown>): { draft?: string } =>
    typeof search.draft === "string" && search.draft ? { draft: search.draft } : {},
});

const DEFINITIONS_PATH = "/portal/workflows/definitions";

/**
 * A publish whose reconciler refused a trigger, described for the author.
 *
 * The version IS saved — the DSL is valid — but a refused trigger means the
 * workflow is published with something that will never fire. That used to be
 * visible only in service logs, so the author saw a clean success. We stay on
 * the page rather than navigating away on a toast, because a toast is exactly
 * what someone misses when they are already looking at the next thing.
 */
function reconcileWarning(
  reconcile: ReconcileResponse | null | undefined,
): string | null {
  if (!reconcile) return null;
  // Keyed off the failed list, not `ok`: the server derives `ok` as "no
  // trigger failed", so checking both is a branch no test can tell apart.
  const failed = reconcile.triggers.filter((t) => t.action === "failed");
  if (failed.length === 0) return null;
  return (
    `Saved as v${reconcile.version}, but ${failed.length} trigger` +
    `${failed.length === 1 ? "" : "s"} could not be wired: ` +
    failed.map((t) => `${t.trigger_id} — ${t.error}`).join("; ") +
    ". The workflow is published, but that trigger will not fire until it is fixed."
  );
}

/** The active version for a key (or the highest-version row as a fallback) —
 * mirrors the list's groupByKey "primary row" choice so the bump edits what's
 * actually live. */
function pickActive(
  rows: DefinitionResponse[],
  key: string,
): DefinitionResponse | null {
  const forKey = rows
    .filter((r) => r.workflow_key === key)
    .sort((a, b) => b.version - a.version);
  if (forKey.length === 0) return null;
  return forKey.find((r) => r.is_active_version) ?? forKey[0]!;
}

function NewVersionPage() {
  const { key } = Route.useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { isAdmin, isPlatformAdmin } = usePermissions();
  const canManage = isAdmin || isPlatformAdmin;
  const { draft: draftId } = Route.useSearch();
  const draftLoad = useWorkflowDraft(draftId, token, canManage);

  const [rows, setRows] = useState<DefinitionResponse[]>([]);
  const [current, setCurrent] = useState<DefinitionResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [json, setJson] = useState("");
  const [baseJson, setBaseJson] = useState("");
  const [baseName, setBaseName] = useState("");
  const [baseDescription, setBaseDescription] = useState("");
  // Pre-filled from the version being superseded, and editable — this is the
  // staleness valve. Intent lives in the versioned DSL, so without an editable
  // field here a v1 description would ride along unchanged to v6 and quietly
  // describe a workflow that no longer does that.
  const [description, setDescription] = useState("");
  // See handleJsonChange — seeded at prefill so the inherited sentence is not
  // re-adopted over an edit on the first JSON-tab keystroke.
  const [lastAdopted, setLastAdopted] = useState<string | null>(null);

  /** Adopt a `description` that arrives inside the DSL — e.g. when Generate's
   * Apply drops a workflow on the canvas carrying the prompt that produced it.
   *
   * Guarded by `lastAdopted`, and that guard is load-bearing: WorkflowAuthor
   * emits the whole document on every JSON-tab keystroke, so an unguarded
   * version re-adopted the same description continuously and silently reverted
   * whatever the author had typed into the field — including a redaction. Only
   * a description that DIFFERS from the one last adopted is a new arrival. */
  const handleJsonChange = (next: string) => {
    setJson(next);
    try {
      const parsed = JSON.parse(next) as { description?: unknown };
      const incoming =
        typeof parsed.description === "string" ? parsed.description.trim() : "";
      if (incoming && incoming !== lastAdopted) {
        setDescription(incoming);
        setLastAdopted(incoming);
      }
    } catch {
      // Mid-edit JSON is routinely unparseable; nothing to adopt yet.
    }
  };
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Distinct from `error`: the publish SUCCEEDED, so this is not a failure to
  // retry — it is a warning about what was left unwired.
  const [reconcileIssue, setReconcileIssue] = useState<string | null>(null);
  // Set when a publish succeeded but the page stayed (reconcile warning):
  // Publish then waits for a change, so a second click cannot mint an
  // identical duplicate version.
  const [publishedVersion, setPublishedVersion] = useState<number | null>(null);
  // Issues the ENGINE would 422 on (missing required failure exit, duplicate
  // import version key). WorkflowAuthor's Overview "Checks" lists these;
  // publishing stays disabled until they are fixed.
  // Plus the blockers only the canvas can see (duplicate exits — gate
  // R2-M1), which WorkflowAuthor reports up: the JSON has already lost them.
  const [canvasBlockers, setCanvasBlockers] = useState<ValidationIssue[]>([]);
  // Bumped when Publish fails the Name check, so WorkflowAuthor clears the
  // selection and the Overview (where Name lives) shows.
  const [revealOverview, setRevealOverview] = useState(0);
  const publishBlockers = useMemo(
    () => [...publishBlockingIssues(json), ...canvasBlockers],
    [json, canvasBlockers],
  );
  // A half-typed JSON tab is not a workflow — the canvas shows an alert in
  // its place, and Publish waits until the document parses again.
  const jsonUnparseable = useMemo(() => parseWorkflowDsl(json) === null, [json]);

  const backToList = () => navigate({ to: DEFINITIONS_PATH });

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const allRows = await workflowsApi.listDefinitions(token ?? undefined);
      const active = pickActive(allRows, key);
      if (!active) {
        setLoadError(`No workflow definition found for "${key}".`);
        return;
      }
      setRows(allRows);
      setCurrent(active);
      setName(active.name);
      setBaseName(active.name);
      const jsonStr = JSON.stringify(active.workflow, null, 2);
      setJson(jsonStr);
      setBaseJson(jsonStr);
      const existing = (active.workflow as { description?: unknown }).description;
      const existingStr = typeof existing === "string" ? existing : "";
      setDescription(existingStr);
      setBaseDescription(existingStr);
      setLastAdopted(typeof existing === "string" ? existing.trim() : null);
    } catch (e) {
      setLoadError(
        e instanceof Error ? e.message : "Failed to load the workflow definition",
      );
    } finally {
      setIsLoading(false);
    }
  }, [key, token]);

  useEffect(() => {
    if (canManage) void load();
  }, [canManage, load]);

  // Lay a Claude draft over the loaded active version, once. The active
  // version stays the base, so the draft reads as "Unpublished changes" and
  // the person publishes the difference as the next version.
  const [prefilledFrom, setPrefilledFrom] = useState<string | null>(null);
  const [draftMarked, setDraftMarked] = useState(false);
  const draftKeyMismatch =
    draftLoad.draft && draftLoad.draft.workflow_key !== key
      ? `This draft is for "${draftLoad.draft.workflow_key}", not "${key}".`
      : null;
  useEffect(() => {
    const draft = draftLoad.draft;
    if (!draft || !current || draftKeyMismatch || prefilledFrom === draft.id) return;
    const fill = draftPrefill(draft);
    setName(fill.name);
    handleJsonChange(fill.json);
    setPrefilledFrom(draft.id);
    // handleJsonChange is re-created each render; the guard above is what
    // keeps this to one fill per draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftLoad.draft, current, draftKeyMismatch, prefilledFrom]);

  /** Make `row` the base the page edits and compares against: its DSL,
   * name and description become both the live fields and the "No changes"
   * reference (D3). Used by the version selector (D4) and after a publish
   * that stays on the page (a reconcile warning), where the just-published
   * version IS the new base — leaving the old one in place read "Unpublished
   * changes" beside "the workflow is published" and kept Publish live for a
   * duplicate version (gate S4). */
  const adoptBase = useCallback((row: DefinitionResponse) => {
    setCurrent(row);
    setName(row.name);
    setBaseName(row.name);
    const jsonStr = JSON.stringify(row.workflow, null, 2);
    setJson(jsonStr);
    setBaseJson(jsonStr);
    const existing = (row.workflow as { description?: unknown }).description;
    const existingStr = typeof existing === "string" ? existing : "";
    setDescription(existingStr);
    setBaseDescription(existingStr);
    setLastAdopted(typeof existing === "string" ? existing.trim() : null);
  }, []);

  // D4 — switching versions loads that version's DSL/name/description as the
  // new base. Disabled while there are unpublished changes (checked by the
  // caller via `versionSelectorDisabled` below).
  const onSelectVersion = useCallback(
    (version: number) => {
      const row = rows.find((r) => r.workflow_key === key && r.version === version);
      if (!row) return;
      adoptBase(row);
    },
    [rows, key, adoptBase],
  );

  // D3 — structural, not textual: the canvas's mount write-back re-serialises
  // the DSL (empty `next`/`config` dropped), so a plain string compare read
  // "Unpublished changes" on an untouched Retsol page. See workflowJsonEquivalent.
  const hasUnpublishedChanges =
    !workflowJsonEquivalent(json, baseJson) ||
    name !== baseName ||
    description !== baseDescription;

  const handleBump = async () => {
    if (publishBlockers.length > 0) {
      setError(
        `Fix ${publishBlockers.length} issue${publishBlockers.length === 1 ? "" : "s"} listed in the Overview's Checks before publishing.`,
      );
      return;
    }
    if (!name.trim()) {
      setError("Name is required");
      // The Name field lives in the Overview — bring it into view, as the
      // new-workflow page does (gate R2 NIT).
      setRevealOverview((n) => n + 1);
      return;
    }
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(json) as Record<string, unknown>;
    } catch {
      setError("Workflow JSON failed to parse");
      return;
    }

    setIsSaving(true);
    setError(null);
    setReconcileIssue(null);
    try {
      if (description.trim()) {
        parsed.description = description.trim();
      } else {
        delete parsed.description;
      }
      const body: NewVersionRequest = { name: name.trim(), workflow: parsed };
      const bumped = await workflowsApi.newDefinitionVersion(
        key,
        body,
        token ?? undefined,
      );
      // Once only: after a publish that stays on the page (reconcile warning)
      // a later version is the person's own, not the draft's.
      if (prefilledFrom && !draftMarked) {
        await markDraftPublished(token, prefilledFrom, bumped.id);
        setDraftMarked(true);
      }
      const warning = reconcileWarning(bumped.reconcile);
      if (warning) {
        // Published, staying on the page: the new version is the base now.
        const published: DefinitionResponse = {
          ...(current as DefinitionResponse),
          ...bumped,
          name: bumped.name ?? body.name,
          workflow: bumped.workflow ?? parsed,
        };
        setRows((prev) => [
          ...prev.filter((r) => !(r.workflow_key === key && r.version === published.version)),
          published,
        ]);
        adoptBase(published);
        setPublishedVersion(published.version);
        setReconcileIssue(warning);
        return;
      }
      // The server allocates the version (max + 1), so the toast reads it
      // from the response rather than deriving it from the selected base.
      toast.success(`Bumped ${key} to v${bumped.version}`);
      void backToList();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to bump version");
    } finally {
      setIsSaving(false);
    }
  };

  if (!canManage) {
    return (
      <>
        <Helmet>
          <title>New workflow version | Portal</title>
        </Helmet>
        <PermissionDenied
          message="Workflows are managed by organisation owners and admins. You don't have access to this area."
          backTo={DEFINITIONS_PATH}
          backLabel="Back to definitions"
        />
      </>
    );
  }

  const waitingForDraft =
    Boolean(draftId) &&
    (draftLoad.loading ||
      (draftLoad.draft !== null && !draftKeyMismatch && prefilledFrom !== draftLoad.draft.id));
  if (isLoading || (waitingForDraft && !loadError)) {
    return (
      <>
        <Helmet>
          <title>New workflow version | Portal</title>
        </Helmet>
        <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading current version…
        </div>
      </>
    );
  }

  if (loadError || !current) {
    return (
      <>
        <Helmet>
          <title>New workflow version | Portal</title>
        </Helmet>
        <div className="space-y-3 p-6">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 text-muted-foreground"
            onClick={backToList}
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to definitions
          </Button>
          <Alert variant="destructive">
            <AlertDescription>{loadError}</AlertDescription>
          </Alert>
        </div>
      </>
    );
  }

  const versions = Array.from(
    new Map(
      rows
        .filter((r) => r.workflow_key === key)
        .map((r) => [r.version, { version: r.version, isActive: r.is_active_version }]),
    ).values(),
  ).sort((a, b) => b.version - a.version);

  // The kill switch lives on the ACTIVE version (DefinitionResponse
  // .is_enabled), and the server numbers a new version max(existing) + 1 —
  // neither follows the version picked in the selector (gate S3).
  const activeRow = pickActive(rows, key) ?? current;
  const status: WorkflowStatusLabel = activeRow.is_enabled ? "Active" : "Paused";
  const nextVersion = Math.max(0, ...versions.map((v) => v.version)) + 1;
  const publishWaitsForChange = publishedVersion !== null && !hasUnpublishedChanges;

  const header = (
    <WorkflowEditorHeader
      name={name}
      workflowKey={key}
      status={status}
      versions={versions}
      selectedVersion={current.version}
      onSelectVersion={onSelectVersion}
      versionSelectorDisabled={hasUnpublishedChanges}
      versionSelectorDisabledReason={
        hasUnpublishedChanges
          ? "Finish or discard your unpublished changes before switching versions"
          : undefined
      }
    />
  );

  const publishAction = (
    <Button
      size="sm"
      aria-label={`Publish v${nextVersion}`}
      onClick={() => void handleBump()}
      disabled={
        isSaving || publishBlockers.length > 0 || jsonUnparseable || publishWaitsForChange
      }
      title={
        jsonUnparseable
          ? "Fix the workflow JSON before publishing"
          : publishWaitsForChange
            ? `v${publishedVersion} is published — make a change to publish again`
            : undefined
      }
    >
      {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
      Publish
      {publishBlockers.length > 0 ? (
        <span
          data-testid="publish-blocked"
          className="ml-1.5 rounded-full bg-canvas-error-tint px-1.5 text-[10px] text-canvas-error"
        >
          {publishBlockers.length}
        </span>
      ) : null}
    </Button>
  );

  const draftProblem = draftLoad.error ?? draftKeyMismatch;
  // Ranked by what the person most needs to see: a failed publish, then a
  // publish that saved with an unwired trigger, then the draft-link notice.
  // The draft notice never clears, so ranking it above either outcome hid
  // that outcome entirely (gate F1, and the re-check's reconcile case).
  const canvasAlert = error ? (
    <Alert variant="destructive" className="rounded-none border-x-0 border-t-0">
      <AlertDescription>{error}</AlertDescription>
    </Alert>
  ) : reconcileIssue ? (
    <Alert data-testid="reconcile-warning" className="rounded-none border-x-0 border-t-0">
      <AlertTriangle className="h-4 w-4" />
      <AlertDescription>
        {reconcileIssue}{" "}
        <button type="button" className="underline underline-offset-2" onClick={backToList}>
          Back to definitions
        </button>
      </AlertDescription>
    </Alert>
  ) : draftProblem ? (
    <Alert variant="destructive" className="rounded-none border-x-0 border-t-0">
      <AlertDescription>{draftProblem}</AlertDescription>
    </Alert>
  ) : prefilledFrom && !draftMarked ? (
    <Alert data-testid="draft-prefill-notice" className="rounded-none border-x-0 border-t-0">
      <Sparkles className="h-4 w-4" />
      <AlertDescription>
        Claude's draft is laid over v{current?.version}. Nothing runs until you
        publish it as the next version — check every node first.
      </AlertDescription>
    </Alert>
  ) : null;

  return (
    <>
      <Helmet>
        <title>New workflow version | Portal</title>
      </Helmet>
      {/* D18: cancel the portal layout's padding and fill it exactly — same
          technique as RelationshipModelPage.tsx, minus the TOP margin: this
          route sits under the workflows sub-nav (workflows.tsx), so above it
          is the sub-nav and its gap, not `<main>`'s padding — a negative top
          margin overlapped the sub-nav by 8px (proof P1, 2026-10-11). */}
      <div className="-mx-3 -mb-3 h-[calc(100%+0.75rem)] min-h-[560px] md:-mx-6 md:-mb-6 md:h-[calc(100%+1.5rem)]">
        <WorkflowAuthor
          onCanvasPublishBlockersChange={setCanvasBlockers}
          workflowJson={json}
          onWorkflowJsonChange={handleJsonChange}
          workflowKey={key}
          header={header}
          publishAction={publishAction}
          saveStateLabel={hasUnpublishedChanges ? "Unpublished changes" : "No changes"}
          canvasAlert={canvasAlert}
          baseSummary={current.summary}
          baseWorkflowJson={baseJson}
          revealOverview={revealOverview}
          overviewEditable={{
            name,
            onNameChange: setName,
            description,
            onDescriptionChange: setDescription,
          }}
        />
      </div>
    </>
  );
}
